/**
 * Created by Ning Jiang on 10/31/2019.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {NudgeStackedSymbolWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/nudge/NudgeStackedSymbolWPController";
import {TimelineLite} from "gsap";
import {DeadwoodSoundConfig} from "../../DeadwoodSoundConfig";
import {DeadwoodGameMode} from "../../gamemode/DeadwoodGameMode";
import {DeadwoodMultiplierController} from "../../multiplier/DeadwoodMultiplierController";
import {DeadwoodReelView} from "../../reel/DeadwoodReelView";
import {DeadwoodParsedGameData} from "../../server/data/DeadwoodParsedGameData";
import {DeadwoodStackedWild} from "../../symbols/DeadwoodStackedWild";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";

export class NudgeWildWPController extends NudgeStackedSymbolWPController {

    private _shootStates:boolean[];
    private _shootTimelines:TimelineLite[];
    private _nudgeCompleted:boolean = true;
    private _shootDelay:number[];
    private _abortSoundPlayed:boolean = false;
    private _completed:boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes, {nudgeStepSpeed : 0.6, stackedSymbolKeyword : "W"});
    }

    protected isNoWinNudge(data:DeadwoodParsedGameData):boolean {
        if(data.mode === DeadwoodGameMode.FREESPIN_GUNSLINGER) {
            return false;
        }
        const hasWin:boolean = this._betWinsData != null && this._betWinsData.length > 0;
        return !hasWin;
    }

    protected startWinPresentation():void {
        this._nudgeCompleted = false;
        this._abortSoundPlayed = false;
        this._completed = false;
        super.startWinPresentation();
    }

    protected getNextReelNudgeDelayDelta(reelId:number):number {
        this._shootDelay[reelId] = Math.random() * 0.2 + 0.3;
        return Math.abs(this._steps[reelId]) * this._nudgeConfig.nudgeStepSpeed + this._shootDelay[reelId];
    }

    protected onNudgeStepStart(reelId:number, stepLeft:number):void {
        super.onNudgeStepStart(reelId, stepLeft);

        if(this.getReelNoWinNudge(reelId) && this._nudgedSteps[reelId] === 0) {
            // play abort sound for no win nudge.
            this.playNudgeAbortSound();
        }
    }

    protected playNudgeStepStart(reelId:number, stepsLeft:number):void {
        const step:number = Math.abs(stepsLeft);
        if(step > 0) {
            SlotGame.sound.playEffect((<any>DeadwoodSoundConfig.instance)[`WILD_NUDGE_${step}`]);
        }
    }

    protected playOnNudgeStepComplete(reelId:number, stepsLeft:number):void {
        // the second position from the nudge direction is always the Wild
        const symbolId:number = stepsLeft >= 0 ? 1 : 2;
        const wild:DeadwoodStackedWild = <DeadwoodStackedWild>Reels.getSymbol(reelId, symbolId);
        if(wild) {
            // Logger.logDev(`playOnNudgeStepComplete multiplier ${Math.abs(this._nudgedSteps[reelId]) + 1}`);
            wild.stack!.symbols.forEach((symbol:StateReelSymbol | null) => {
                (<DeadwoodStackedWild>symbol)?.updateMultiplier(Math.abs(this._nudgedSteps[reelId]) + 1, true, "normal");
            });
        }
        EventHandler.dispatchEvent(new GameEvent(DeadwoodMultiplierController.ADD_MULTIPLIER));
    }

    protected playOnReelNudgeComplete(reelId:number, aborted:boolean):void {
        // When nudge completed the symbol[1] is always Wild
        const wild:DeadwoodStackedWild = <DeadwoodStackedWild>Reels.getSymbol(reelId, 1);
        if(wild) {
            if(this._steps[reelId] > 0) {
                wild.stack!.symbols.forEach((symbol:StateReelSymbol | null) => {

                    (<DeadwoodStackedWild>symbol)?.updateMultiplier(Math.abs(this._nudgedSteps[reelId]) + 1, true, "normal");

                });
            }

            if(this.getReelNoWinNudge(reelId)) {
                return;
            }

            const timeline:TimelineLite = new TimelineLite();
            timeline.timeScale(aborted ? 20 : 1);
            timeline.add(this.playGunShot(wild, true));
            timeline.add(() => {
                this._shootStates[reelId] = false;
                this.checkComplete();
            });
            this._shootTimelines[reelId] = timeline;
            this._shootStates[reelId] = true;
        }
    }

    protected playGunShot(wild:DeadwoodStackedWild, animate:boolean):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => (<DeadwoodReelView>Reels.getReel(wild.reelId)!.view).addSymbolToWinContainer(wild),
            wild.playGunShot(animate, this._shootDelay[wild!.reelId])
        ]);

        return timeline;
    }

    protected playOnAllNudgeComplete():void {
        this._nudgeCompleted = true;
        this.checkComplete();
    }

    protected checkComplete():void {
        if(this._completed) {
            return;
        }

        if(!this._nudgeCompleted || this._shootStates.some(state => state)) {
            return;
        }

        this.finish();
    }

    protected stopWinPresentation(isAborted:boolean):void {
        this._completed = true;
        EventHandler.dispatchEvent(new GameEvent(DeadwoodMultiplierController.SHOW_END_MULTIPLIER, isAborted && !this._noWinNudge));
        super.stopWinPresentation(isAborted);

        // This is the first wp, so the startWP will always be triggered, no need to think about abort before start.
    }

    public abort():void {
        if(this._isPlaying && this._skippable) {
            super.abort();

            // for the nudge is already finished but shooting is not.
            this._shootTimelines.forEach((shootingTimeline:TimelineLite, reelId:number) => {
                if(shootingTimeline && shootingTimeline.isActive()) {
                    shootingTimeline.timeScale(20);
                }
            });
        }
    }

    protected abortNudgingReel(reelId:number):void {
        super.abortNudgingReel(reelId);
        const symbolId:number = this._steps[reelId] >= 0 ? 1 : 2;
        const wild:DeadwoodStackedWild = <DeadwoodStackedWild>Reels.getSymbol(reelId, symbolId);
        if(wild) {
            // Logger.logDev(`abortNudgingReel end multiplier ${Math.abs(this._steps[reelId]) + 1}`);
            wild.stack!.symbols.forEach((symbol:StateReelSymbol | null) => {
                (<DeadwoodStackedWild>symbol)?.updateMultiplier(Math.abs(this._steps[reelId]) + 1, true, "normal");
            });
        }
    }

    protected playNudgeAbortSound():void {
        if(this._abortSoundPlayed) {
            return;
        }
        SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.WILD_NUDGE_ABORT);
        SlotGame.sound.stopEffect(DeadwoodSoundConfig.instance.WILD_NUDGE_1);
        SlotGame.sound.stopEffect(DeadwoodSoundConfig.instance.WILD_NUDGE_2);
        SlotGame.sound.stopEffect(DeadwoodSoundConfig.instance.WILD_NUDGE_3);
        this._abortSoundPlayed = true;
    }

    protected resetFeature():void {
        super.resetFeature();

        this._shootTimelines = [];
        this._shootStates = ArrayHelper.initArrayWithValues<boolean>(GameConfig.instance.REELS_NUM, () => false);
        this._shootDelay = ArrayHelper.initArrayWithValues(GameConfig.instance.REELS_NUM, () => 0)
    }
}